<script setup lang="ts">
import { useFetch } from '@/composables/useFetch';
import { useEnvStore } from '@/stores/envStore';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import PlanCard from '@/components/Plan/PlanCard.vue';
import PlanComparisonTable from '@/components/PlanComparisonTable.vue';
import { useExternalUrl } from '@/composables/useExternalUrl';
import { useInAppPurchase } from '@/composables/useInAppPurchase';
import { usePlatform } from '@/composables/usePlatform';
import { useSubscription } from '@/composables/useSubscription';
import router from '@/router';
import { useUserStore } from '@/stores/useUserStore';
import type { SubscriptionPlan } from '@/types/api';
import Button from 'primevue/button';
import InputSwitch from 'primevue/inputswitch';
// import Message from 'primevue/message';
import TabPanel from 'primevue/tabpanel';
import TabView from 'primevue/tabview';
import type { Ref } from 'vue';

const { fetchData } = useFetch();
const toast = useToast();
const { t } = useI18n();
const { navigateToExternalUrl } = useExternalUrl();
const { isNative, isIOS } = usePlatform();
const {
  initializeStore,
  isInitialized,
  isPlanAvailableForPurchase,
  redeemOfferCode,
  openManageSubscriptions,
  isProcessing: isRedeemingCode,
} = useInAppPurchase();
const plans: Ref<SubscriptionPlan[]> = ref([]);
const envStore = useEnvStore();
const selectedPlan: Ref<SubscriptionPlan | null> = ref(null);
const activeTab = ref(0);
const isYearly = ref(true);

const currentActivePlanLocalId = ref<number | null>(null);
const isModifyingSubscription = ref(false);
const isLoadingChange = ref(false);
const isRedirectingToPortal = ref(false);

// Add comprehensive loading states
const isInitialLoading = ref(true);
const isLoadingValidation = ref(false);

const { ensureTeamDataLoaded, fetchCurrentPlan, canDowngradeToPlan, loading: subscriptionLoading } = useSubscription();
const userStore = useUserStore();

// Check if subscription change should be blocked (native iOS with Apple subscription)
const isSubscriptionChangeBlocked = computed(() => {
  const hasAppleSubscription = userStore.user?.active_restaurant?.subscription?.platform === 'apple';
  return isNative.value && isIOS.value && hasAppleSubscription && isModifyingSubscription.value;
});

// Computed to check if any loading is happening
const isAnyLoading = computed(
  () => isInitialLoading.value || isLoadingValidation.value || subscriptionLoading.value || isLoadingChange.value || isRedirectingToPortal.value,
);

const fetchCurrentSubscriptionStatus = async () => {
  const { data } = await fetchData(`${envStore.apiUrl}/subscription-details`);
  if (data && data.plan && data.status !== 'ELIGIBLE_FOR_INITIAL_TRIAL' && data.status !== 'LAPSED_NO_ACTIVE_SUBSCRIPTION') {
    console.log('Current active plan data:', data);
    currentActivePlanLocalId.value = data.plan.id;
    isModifyingSubscription.value = true;
  } else {
    currentActivePlanLocalId.value = null;
    isModifyingSubscription.value = false;
  }
};

const fetchPlans = async () => {
  const { data } = await fetchData(`${envStore.apiUrl}/plans`);
  if (data) {
    plans.value = data as SubscriptionPlan[];
  } else {
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('subscription.errorFetchingPlans'),
      life: 3000,
    });
  }
};

const filteredPlans = computed(() => {
  return plans.value
    .filter((plan) => {
      // Existing price check
      const hasPrice =
        plan.name.toLowerCase() === 'freemium' || (isYearly.value ? (Number(plan.price_yearly) || 0) > 0 : (Number(plan.price_monthly) || 0) > 0);

      if (!hasPrice) return false;

      // On native iOS, also check if IAP product is available
      if (isNative.value && isIOS.value && isInitialized.value) {
        const billingPeriod = isYearly.value ? 'yearly' : 'monthly';
        return isPlanAvailableForPurchase(plan, billingPeriod);
      }

      return true;
    })
    .sort((a, b) => a.order - b.order);
});

const plansByCategory = computed(() => {
  const grouped: Record<string, SubscriptionPlan[]> = {};
  for (const plan of filteredPlans.value) {
    if (!grouped[plan.category]) grouped[plan.category] = [];
    grouped[plan.category].push(plan);
  }
  Object.keys(grouped).forEach((cat) => {
    grouped[cat].sort((a, b) => a.order - b.order);
  });
  return grouped;
});

const selectPlan = async (plan: SubscriptionPlan) => {
  if (isAnyLoading.value) return; // Prevent selection during loading

  selectedPlan.value = plan;
  await handleProceedWithSelectedPlan();
};

const navigateToSubscriptionConfirmation = () => {
  if (!selectedPlan.value) return;

  router.push({
    name: 'subscription-plan-confirm',
    params: { planId: selectedPlan.value.id.toString() },
    query: {
      billing_period: isYearly.value ? 'yearly' : 'monthly',
    },
  });
};

const handleProceedWithSelectedPlan = async () => {
  if (!selectedPlan.value || isAnyLoading.value) return;

  // Only show loading if we're modifying subscription and need validation
  if (isModifyingSubscription.value) {
    isLoadingValidation.value = true;
  }

  try {
    if (isModifyingSubscription.value) {
      // These are now very fast if data already exists
      await Promise.all([ensureTeamDataLoaded(), fetchCurrentPlan()]);
    }

    navigateToSubscriptionConfirmation();
  } finally {
    isLoadingValidation.value = false;
  }
};

const redirectToBillingPortal = async () => {
  if (isAnyLoading.value) return; // Prevent action during loading

  isRedirectingToPortal.value = true;
  try {
    const { data, error } = await fetchData(`${envStore.apiUrl}/billing`, 'GET');
    console.log(data);
    if (error || !data) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('subscription.billingPortalError'),
        life: 3000,
      });
    } else {
      if (isNative.value) {
        await navigateToExternalUrl(data.url, { target: '_blank' });
      } else {
        await navigateToExternalUrl(data.url);
      }
    }
  } catch (e) {
    console.error('Error redirecting to billing portal:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('subscription.billingPortalError'),
      life: 3000,
    });
  } finally {
    isRedirectingToPortal.value = false;
  }
};

// Check if a plan can be selected based on downgrade restrictions
const canSelectPlan = (plan: SubscriptionPlan) => {
  if (isAnyLoading.value) return false; // Disable during loading
  if (!isModifyingSubscription.value) return true;
  if (plan.id === currentActivePlanLocalId.value) return true;

  const { canDowngrade } = canDowngradeToPlan(plan);
  return canDowngrade;
};

const initializeData = async () => {
  isInitialLoading.value = true;

  try {
    // Initialize IAP store for iOS native
    if (isNative.value && isIOS.value) {
      await initializeStore();
    }

    // Always fetch core subscription data
    await Promise.all([fetchPlans(), fetchCurrentSubscriptionStatus()]);

    // Pre-load team data for subscription changes (very fast if already cached)
    if (isModifyingSubscription.value) {
      await Promise.all([
        ensureTeamDataLoaded(), // Returns immediately if data exists
        fetchCurrentPlan(), // Returns immediately if already fetched
      ]);
    }
  } catch (error) {
    console.error('Error initializing data:', error);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('subscription.errorInitializing'),
      life: 3000,
    });
  } finally {
    isInitialLoading.value = false;
  }
};

onMounted(initializeData);
</script>

<template>
  <div>
    <!-- Native Message with Magic Link -->
    <!-- <div v-if="isNative" class="mx-auto flex max-w-2xl flex-col gap-6 p-6">
      <div class="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center">
        <i class="pi pi-info-circle mb-3 text-4xl text-blue-600"></i>
        <h3 class="mb-2 font-semibold text-blue-900">{{ t('subscription.iosManageTitle') }}</h3>
        <p class="mb-4 text-blue-800">{{ t('subscription.iosManageMessage') }}</p>
        <Button
          :label="t('subscription.magicLink.generate')"
          icon="pi pi-link"
          severity="info"
          :loading="isGeneratingMagicLink"
          @click="() => generateAndOpenMagicLink('/subscription/plans')"
        />
      </div>
    </div> -->

    <!-- Loading overlay for initial data fetch -->
    <div v-if="isInitialLoading" class="flex min-h-[400px] items-center justify-center">
      <BaseLoader />
    </div>

    <div v-else>
      <!-- <Message severity="info" :closable="false">
        {{
          t('subscription.enjoyCouponLaunch', {
            discount: 20,
          })
        }}
      </Message> -->

      <!-- iOS App Store pricing warning -->
      <!-- <div v-if="isNative && isIOS" class="mx-auto my-4 max-w-2xl rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div class="flex items-start gap-3">
          <i class="pi pi-info-circle mt-0.5 text-lg text-amber-600"></i>
          <div>
            <p class="font-medium text-amber-900">{{ t('subscription.iap.applePricingWarningShort') }}</p>
            <p class="mt-1 text-sm text-amber-800">{{ t('subscription.iap.applePricingWarning') }}</p>
          </div>
        </div>
      </div> -->

      <!-- Manage billing button - only for Stripe subscriptions -->
      <div v-if="useUserStore().user?.active_restaurant?.subscription?.platform === 'stripe'" class="mx-auto my-6 flex items-center justify-center">
        <Button
          :label="t('subscription.manageBilling')"
          icon="pi pi-external-link"
          :loading="isRedirectingToPortal"
          :disabled="isAnyLoading"
          outlined
          size="small"
          @click="redirectToBillingPortal"
        />
      </div>

      <!-- Apple subscription management notice -->
      <div
        v-else-if="useUserStore().user?.active_restaurant?.subscription?.platform === 'apple'"
        class="mx-auto my-4 max-w-md rounded-lg border border-blue-200 bg-blue-50 p-4 text-center"
      >
        <div class="flex items-center justify-center gap-2">
          <i class="pi pi-apple text-blue-600"></i>
          <span class="text-sm text-blue-800">{{ t('subscription.manageApple') }}</span>
        </div>
      </div>

      <!-- Apple Subscription Change Blocked Message for native iOS -->
      <div v-if="isSubscriptionChangeBlocked" class="mx-auto my-6 max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <i class="pi pi-apple mb-3 text-4xl text-red-600"></i>
        <h3 class="mb-2 font-semibold text-red-900">{{ t('subscription.iap.cannotChangeSubscription') }}</h3>
        <p class="mb-4 text-red-800">{{ t('subscription.iap.manageAppleSubscription') }}</p>
        <Button
          :label="t('subscription.iap.openAppStoreSettings')"
          icon="pi pi-external-link"
          severity="danger"
          outlined
          @click="openManageSubscriptions()"
        />
      </div>

      <!-- Apple Offer Code Redemption (iOS native only) -->
      <div v-if="isNative && isIOS && !isSubscriptionChangeBlocked" class="mx-auto my-4 flex max-w-md justify-center">
        <Button
          :label="t('subscription.iap.offerCode.haveACode')"
          icon="pi pi-tag"
          text
          size="small"
          :disabled="isAnyLoading || isRedeemingCode"
          :loading="isRedeemingCode"
          @click="redeemOfferCode"
        />
      </div>

      <TabView v-model:active-index="activeTab" :pt="{ inkbar: { class: 'hidden' } }" :pt-options="{ mergeProps: true }" :disabled="isAnyLoading">
        <TabPanel
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: { class: '!justify-center' },
            content: { class: '!px-0' },
          }"
          :pt-options="{ mergeProps: true }"
          :header="t('subscription.tabs.list')"
        >
          <!-- Pricing Toggle -->
          <div class="flex h-8 items-center justify-center gap-4">
            <span
              class="text-sm text-primary-900 transition-colors"
              :class="{
                'font-semibold': !isYearly,
                'cursor-pointer': !isAnyLoading,
                'cursor-not-allowed opacity-50': isAnyLoading,
              }"
              @click="!isAnyLoading && (isYearly = false)"
            >
              {{ t('subscription.monthly') }}
            </span>
            <InputSwitch
              v-model="isYearly"
              :label="t('subscription.yearly')"
              :off-label="t('subscription.monthly')"
              :disabled="isAnyLoading"
              class="w-16"
              input-id="switchYearlyMonthly"
            />
            <span
              class="text-sm text-primary-900 transition-colors"
              :class="{
                'font-semibold': isYearly,
                'cursor-pointer': !isAnyLoading,
                'cursor-not-allowed opacity-50': isAnyLoading,
              }"
              @click="!isAnyLoading && (isYearly = true)"
            >
              {{ t('subscription.yearly') }}
            </span>
          </div>

          <!-- Loading indicator for validation -->
          <div class="my-2 flex h-8 items-center justify-center">
            <div v-if="isLoadingValidation" class="flex items-center gap-2 text-sm text-primary-700">
              <i class="pi pi-spin pi-spinner"></i>
              {{ t('subscription.validating') }}
            </div>
          </div>

          <div class="items-left mx-auto flex max-w-md flex-col gap-8">
            <div v-for="(thePlans, category) in plansByCategory" :key="category" class="mb-8">
              <div class="mb-4 flex flex-col items-start gap-4 font-semibold text-base text-primary-900">
                <span>{{ t(`subscription.category.${category}`) }}</span>
                <span v-if="category === 'business'" class="rounded-md bg-green-100 px-2 py-1 text-xs text-green-800">
                  {{ t('subscription.saveWithYearly') }}
                </span>
              </div>
              <div class="flex w-full flex-row flex-wrap gap-6">
                <PlanCard
                  v-for="thePlan in thePlans"
                  :key="thePlan.id"
                  :plan="thePlan"
                  :selected="currentActivePlanLocalId === thePlan.id"
                  :is-current-active-plan="thePlan.id === currentActivePlanLocalId"
                  :disabled="!canSelectPlan(thePlan)"
                  :is-yearly="isYearly"
                  :class="{
                    'pointer-events-none': isAnyLoading,
                  }"
                  @select="selectPlan"
                />
              </div>
            </div>
          </div>
        </TabPanel>

        <TabPanel
          :header="t('subscription.tabs.comparison')"
          :pt="{
            header: { class: '!w-1/2' },
            headerAction: { class: '!justify-center' },
            content: { class: '!px-0' },
          }"
          :pt-options="{ mergeProps: true }"
        >
          <!-- Pricing Toggle -->
          <div class="flex h-8 items-center justify-center gap-4">
            <span
              class="text-sm text-primary-900 transition-colors"
              :class="{
                'font-semibold': !isYearly,
                'cursor-pointer': !isAnyLoading,
                'cursor-not-allowed opacity-50': isAnyLoading,
              }"
              @click="!isAnyLoading && (isYearly = false)"
            >
              {{ t('subscription.monthly') }}
            </span>
            <InputSwitch
              v-model="isYearly"
              :label="t('subscription.yearly')"
              :off-label="t('subscription.monthly')"
              :disabled="isAnyLoading"
              class="w-16"
            />
            <span
              class="text-sm text-primary-900 transition-colors"
              :class="{
                'font-semibold': isYearly,
                'cursor-pointer': !isAnyLoading,
                'cursor-not-allowed opacity-50': isAnyLoading,
              }"
              @click="!isAnyLoading && (isYearly = true)"
            >
              {{ t('subscription.yearly') }}
            </span>
          </div>
          <div class="my-2 flex h-8 items-center justify-center">
            <span v-if="isYearly" class="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
              {{ t('subscription.saveWithYearly') }}
            </span>
          </div>
          <PlanComparisonTable :plans="plans" :current-plan-id="currentActivePlanLocalId" :is-yearly="isYearly" :disabled="isAnyLoading" />
        </TabPanel>
      </TabView>
    </div>
  </div>
</template>
